/**
 * Plan-tree geometry.
 *
 * These are *structural* dimensions, not spacing: the drag-to-reorder maths, the
 * row indent and the ghost-add row's left padding all have to agree to the pixel
 * or a dragged row lands at the wrong depth and the tree looks misaligned. That
 * is why they are named constants rather than values snapped onto the 8pt grid —
 * rounding `22` to `24` here would silently break depth detection.
 *
 * They were previously duplicated as bare literals: `INDENT_PX` in PlanRow,
 * a second copy in useDragReorder, and `pl: '42px'` written out by hand in
 * PlanPanel to match `DEPTH_BASE_PX`.
 */

/** Horizontal offset per nesting level. */
export const INDENT_PX = 22;

/** Row left padding (16) + select checkbox (20) + gap — where depth 0 content starts. */
export const DEPTH_BASE_PX = 42;

/**
 * Estimated row height the virtualizer starts from before measuring.
 *
 * Measured, not guessed: a leaf is 72 and a parent 55. Every staffable row now
 * carries a second line (the resources cell — avatars, or the dashed "Add
 * resources"), so 55 describes only a parent, and leaves dominate a plan. The
 * previous 44 matched neither even before that: a plain row was already 55.
 * `measureElement` corrects both, so this only governs the first paint.
 */
export const ROW_ESTIMATE_PX = 72;

/** How close to the edge a drag must get before the scroller starts moving. */
export const AUTO_SCROLL_MARGIN_PX = 56;

/** Pixels scrolled per pointer-move while auto-scrolling. */
export const AUTO_SCROLL_STEP_PX = 14;

/**
 * Left indent that lines content up under a depth-0 task *label* — past the
 * select checkbox and the fold chevron. Used by the keyboard hint bar so its
 * shortcuts sit under the title they describe.
 */
export const LABEL_INDENT_PX = 56;

/** Fixed widths of the tree's right-hand columns, matched by header and row. */
export const COLUMN_WIDTH = {
  hours: 78,
  /** The start date (Mette, 3 Nov), where the Status column was. */
  startDate: 110,
  targetDate: 110,
  /** Kebab (24) + drag handle (24) + the row gap between them. */
  trailing: 52,
  select: 20,
} as const;
